import {Caption, Word} from '@contentlab/types/captions';
import {SrtItem} from '@contentlab/utils/srtUtils';

export const MAX_REMAINING_UNMATCHED_WORDS_IN_EDITED_CAPTION = 5;

export const getTimedWords = (caption: Caption): Word[] => {
  if (!caption?.text?.trim()) return [];
  if (caption.wordTimings) return caption.wordTimings;

  return getUniformlyTimedWords(caption);
};

export const calculateSmartWordTimings = (
  editedText: string,
  originalSrtItem: SrtItem
): Word[] => {
  const {wordTimings} = originalSrtItem;
  if (!wordTimings || wordTimings.length === 0) {
    return getUniformlyTimedWords({
      id: originalSrtItem.id,
      text: editedText,
      start: originalSrtItem.startSeconds,
      end: originalSrtItem.endSeconds,
    });
  }

  return getSmartTimedWords(editedText, originalSrtItem);
};

interface MatchingState {
  currentEditedIndex: number;
  currentOriginalIndex: number;
  lastMatch: {
    editedIndex: number;
    originalIndex: number;
  } | null;
  mismatchStart: {
    editedIndex: number;
    originalIndex: number;
  } | null;
}

interface IndexRange {
  editedStart: number;
  editedEnd: number;
  originalStart: number;
  originalEnd: number;
}

/**
 * Gets the index range for an injection case (words added without skipping original words)
 */
function getInjectionRange(
  state: MatchingState,
  originalIndex?: number
): IndexRange {
  const targetOriginalIndex = originalIndex ?? state.currentOriginalIndex;
  return {
    editedStart: state.lastMatch?.editedIndex ?? 0,
    editedEnd: state.currentEditedIndex + 1,
    originalStart: state.lastMatch?.originalIndex ?? 0,
    originalEnd: targetOriginalIndex + 1,
  };
}

/**
 * Gets the index range when unmatched word counts match (exact timing preservation)
 */
function getExactMatchRange(
  state: MatchingState,
  originalIndex?: number
): IndexRange {
  if (!state.mismatchStart) {
    throw new Error('mismatchStart must not be null');
  }
  const targetOriginalIndex = originalIndex ?? state.currentOriginalIndex;
  return {
    editedStart: state.mismatchStart.editedIndex,
    editedEnd: state.currentEditedIndex + 1,
    originalStart: state.mismatchStart.originalIndex,
    originalEnd: targetOriginalIndex + 1,
  };
}

/**
 * Gets the index range for just the unmatched section (excluding current match)
 */
function getUnmatchedRange(
  state: MatchingState,
  originalIndex?: number
): IndexRange {
  if (!state.mismatchStart) {
    throw new Error('mismatchStart must not be null');
  }
  const targetOriginalIndex = originalIndex ?? state.currentOriginalIndex;
  return {
    editedStart: state.mismatchStart.editedIndex,
    editedEnd: state.currentEditedIndex,
    originalStart: state.mismatchStart.originalIndex,
    originalEnd: targetOriginalIndex,
  };
}

/**
 * Gets the index range for the current matching word
 */
function getCurrentMatchRange(
  editedIndex: number,
  originalIndex: number
): IndexRange {
  return {
    editedStart: editedIndex,
    editedEnd: editedIndex + 1,
    originalStart: originalIndex,
    originalEnd: originalIndex + 1,
  };
}

/**
 * Calculates the index range for distributing remaining unmatched words at the end of the edited text
 */
function getRemainingIndexRange(
  lastMatch: {editedIndex: number; originalIndex: number} | null,
  editedWordsLength: number,
  originalWordsLength: number
): IndexRange {
  return {
    editedStart: lastMatch?.editedIndex ?? 0,
    editedEnd: editedWordsLength,
    originalStart: lastMatch?.originalIndex ?? 0,
    originalEnd: originalWordsLength,
  };
}

/**
 * Records a new match and clears unmatched section
 */
function recordMatch(
  state: MatchingState,
  editedIndex: number,
  originalIndex: number
): void {
  state.lastMatch = {editedIndex, originalIndex};
  state.mismatchStart = null;
}

function handleMatch(
  state: MatchingState,
  editedWords: Word[],
  originalWordTimings: Word[]
): void {
  if (state.mismatchStart !== null) {
    const unmatchedEditedCount =
      state.currentEditedIndex - state.mismatchStart.editedIndex;
    const unmatchedOriginalCount =
      state.currentOriginalIndex - state.mismatchStart.originalIndex;

    const isInjection = unmatchedOriginalCount === 0;

    if (isInjection) {
      const range = getInjectionRange(state);
      applyUniformTimingToSubarray(editedWords, originalWordTimings, range);
    } else if (unmatchedEditedCount === unmatchedOriginalCount) {
      const range = getExactMatchRange(state);
      applyTimingFromWordSubarray(editedWords, originalWordTimings, range);
    } else {
      const unmatchedRange = getUnmatchedRange(state);
      applyUniformTimingToSubarray(
        editedWords,
        originalWordTimings,
        unmatchedRange
      );
      const matchRange = getCurrentMatchRange(
        state.currentEditedIndex,
        state.currentOriginalIndex
      );
      applyTimingFromWordSubarray(editedWords, originalWordTimings, matchRange);
    }
  } else {
    const range = getCurrentMatchRange(
      state.currentEditedIndex,
      state.currentOriginalIndex
    );
    applyTimingFromWordSubarray(editedWords, originalWordTimings, range);
  }
  recordMatch(state, state.currentEditedIndex, state.currentOriginalIndex);
  state.currentEditedIndex += 1;
  state.currentOriginalIndex += 1;
}

function handleMismatch(
  state: MatchingState,
  editedWords: Word[],
  originalWordTimings: Word[],
  editedNormalizedWords: string[],
  originalNormalizedWords: string[]
): void {
  if (state.mismatchStart === null) {
    state.mismatchStart = {
      editedIndex: state.currentEditedIndex,
      originalIndex: state.currentOriginalIndex,
    };
  }

  const foundMatchInOriginalIndex = findMatchInOriginal(
    editedNormalizedWords[state.currentEditedIndex],
    originalNormalizedWords,
    state.currentOriginalIndex
  );

  if (foundMatchInOriginalIndex !== null) {
    const unmatchedEditedCount =
      state.currentEditedIndex - state.mismatchStart.editedIndex;
    const unmatchedOriginalCount =
      foundMatchInOriginalIndex - state.mismatchStart.originalIndex;

    const isInjection = unmatchedOriginalCount === 0;
    if (isInjection) {
      const range = getInjectionRange(state, foundMatchInOriginalIndex);
      applyUniformTimingToSubarray(editedWords, originalWordTimings, range);
    } else if (unmatchedEditedCount === unmatchedOriginalCount) {
      const range = getExactMatchRange(state, foundMatchInOriginalIndex);
      applyTimingFromWordSubarray(editedWords, originalWordTimings, range);
    } else {
      const unmatchedRange = getUnmatchedRange(
        state,
        foundMatchInOriginalIndex
      );
      applyUniformTimingToSubarray(
        editedWords,
        originalWordTimings,
        unmatchedRange
      );
      const matchRange = getCurrentMatchRange(
        state.currentEditedIndex,
        foundMatchInOriginalIndex
      );
      applyTimingFromWordSubarray(editedWords, originalWordTimings, matchRange);
    }
    recordMatch(state, state.currentEditedIndex, foundMatchInOriginalIndex);
    state.currentOriginalIndex = foundMatchInOriginalIndex + 1;
    state.currentEditedIndex += 1;
  } else {
    state.currentEditedIndex += 1;
  }
}

function findMatchInOriginal(
  editedWord: string,
  originalWords: string[],
  startIndex: number
): number | null {
  for (let i = startIndex; i < originalWords.length; i += 1) {
    if (editedWord === originalWords[i]) {
      return i;
    }
  }
  return null;
}

/**
 * Algorithm Flow:
 * 1. Tokenize and normalize both original and edited text (remove punctuation, lowercase)
 * 2. Word-by-word matching: Walk through edited words and try to match with current position in original
 * 3. On mismatch: Track first mismatch position, look ahead in original array for next match
 * 4. On match after mismatch section:
 *    - If injection (no skipped original words): Uniformly distribute WITH padding of neighboring matched
 *      words (last match + unmatched + current match)
 *    - If same count of unmatched words: Apply exact timing from original words (including current match)
 *    - If different count: Uniformly distribute unmatched words WITHOUT padding, then apply exact timing for current match
 * 5. At end: If same count of remaining words, apply exact timing. Otherwise, if remaining unmatched
 *    edited words > remaining unmatched original words + MAX_REMAINING_UNMATCHED_WORDS, fallback to full uniform distribution.
 *    Otherwise, uniformly distribute remaining words including last match as padding.
 *
 * Known Limitation:
 * Does not handle repeated words well. The algorithm uses a greedy single-pass approach
 * that doesn't backtrack. For example, if edited text has "d a b c e" and original has
 * "a b c d e", the algorithm will match "d" at the wrong position and distribute the entire
 * unmatched section uniformly. The fallback mechanism (MAX_REMAINING_UNMATCHED_WORDS_IN_EDITED_CAPTION) helps
 * mitigate this by falling back to full uniform distribution when too many unmatched words remain.
 */
export const getSmartTimedWords = (
  editedText: string,
  originalSrtItem: SrtItem
): Word[] => {
  if (!editedText?.trim()) return [];

  const originalWordTimings = originalSrtItem.wordTimings;
  if (!originalWordTimings || originalWordTimings.length === 0) {
    return getUniformlyTimedWords({
      id: originalSrtItem.id,
      text: editedText,
      start: originalSrtItem.startSeconds,
      end: originalSrtItem.endSeconds,
    });
  }

  const editedWords = tokenizeWords(editedText);
  const originalWords = originalWordTimings.map((w) => w.text);

  const editedNormalizedWords = editedWords.map(normalizeWord);
  const originalNormalizedWords = originalWords.map(normalizeWord);

  const editedWordObjects: Word[] = editedWords.map((text) => ({
    text,
    start: 0,
    end: 0,
  }));

  const state: MatchingState = {
    currentEditedIndex: 0,
    currentOriginalIndex: 0,
    lastMatch: null,
    mismatchStart: null,
  };

  while (state.currentEditedIndex < editedNormalizedWords.length) {
    const isMatch =
      state.currentOriginalIndex < originalNormalizedWords.length &&
      editedNormalizedWords[state.currentEditedIndex] ===
        originalNormalizedWords[state.currentOriginalIndex];

    if (isMatch) {
      handleMatch(state, editedWordObjects, originalWordTimings);
    } else {
      handleMismatch(
        state,
        editedWordObjects,
        originalWordTimings,
        editedNormalizedWords,
        originalNormalizedWords
      );
    }
  }

  // Handle remaining unmatched words at the end of the edited text
  const lastMatchedEditedIndex = state.lastMatch?.editedIndex ?? null;

  const hasRemainingUnmatched =
    state.mismatchStart !== null ||
    (lastMatchedEditedIndex !== null &&
      state.currentEditedIndex > lastMatchedEditedIndex + 1);

  if (hasRemainingUnmatched) {
    const indexRange = getRemainingIndexRange(
      state.lastMatch,
      editedWords.length,
      originalWords.length
    );

    const remainingUnmatchedEdited =
      indexRange.editedEnd - indexRange.editedStart;
    const remainingUnmatchedOriginal =
      indexRange.originalEnd - indexRange.originalStart;

    if (remainingUnmatchedEdited === remainingUnmatchedOriginal) {
      applyTimingFromWordSubarray(
        editedWordObjects,
        originalWordTimings,
        indexRange
      );
    } else {
      // Safety mechanism: If too many unmatched words remain - fall back to uniform distribution
      if (
        remainingUnmatchedEdited >
        remainingUnmatchedOriginal +
          MAX_REMAINING_UNMATCHED_WORDS_IN_EDITED_CAPTION
      ) {
        return getUniformlyTimedWords({
          id: originalSrtItem.id,
          text: editedText,
          start: originalSrtItem.startSeconds,
          end: originalSrtItem.endSeconds,
        });
      }

      applyUniformTimingToSubarray(
        editedWordObjects,
        originalWordTimings,
        indexRange
      );
    }
  }

  return editedWordObjects;
};

const tokenizeWords = (text: string): string[] => {
  return text
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter((w) => w.length > 0);
};

const normalizeWord = (word: string): string => {
  return word.replace(/[^\p{L}\p{N}]/gu, '').toLowerCase();
};

/**
 * Preserves exact timing from original words when words match exactly.
 * Contrasts with applyUniformTimingToSubarray which redistributes timing uniformly.
 */
export const applyTimingFromWordSubarray = (
  editedWords: Word[],
  originalWordTimings: Word[],
  indexRange: IndexRange
): void => {
  validateIndexRange(
    indexRange,
    editedWords.length,
    originalWordTimings.length
  );

  const {editedStart, editedEnd, originalStart, originalEnd} = indexRange;
  const length = editedEnd - editedStart;

  if (length === 0) return;

  if (length !== originalEnd - originalStart) {
    throw new Error(
      `Range lengths must match: edited length=${length}, original length=${originalEnd - originalStart}`
    );
  }

  for (let i = 0; i < length; i += 1) {
    const originalWord = originalWordTimings[originalStart + i];
    if (!originalWord) {
      throw new Error(`Original word at index ${originalStart + i} not found`);
    }

    editedWords[editedStart + i].start = originalWord.start;
    editedWords[editedStart + i].end = originalWord.end;
  }
};

function validateIndexRange(
  indexRange: IndexRange,
  editedWordsLength: number,
  originalWordTimingsLength: number
): void {
  const {editedStart, editedEnd, originalStart, originalEnd} = indexRange;

  if (editedStart < 0 || editedStart >= editedWordsLength) {
    throw new Error(
      `Invalid editedStart: ${editedStart}. Must be between 0 and ${editedWordsLength - 1}`
    );
  }

  if (editedEnd < editedStart || editedEnd > editedWordsLength) {
    throw new Error(
      `Invalid editedEnd: ${editedEnd}. Must be between ${editedStart} and ${editedWordsLength}`
    );
  }

  if (originalStart < 0 || originalStart >= originalWordTimingsLength) {
    throw new Error(
      `Invalid originalStart: ${originalStart}. Must be between 0 and ${originalWordTimingsLength - 1}`
    );
  }

  if (originalEnd < originalStart || originalEnd > originalWordTimingsLength) {
    throw new Error(
      `Invalid originalEnd: ${originalEnd}. Must be between ${originalStart} and ${originalWordTimingsLength}`
    );
  }
}

/**
 * Uniformly distributes timing for a subarray of the edited words over the time duration
 * of a corresponding subarray of original words.
 */
export const applyUniformTimingToSubarray = (
  editedWords: Word[],
  originalWordTimings: Word[],
  indexRange: IndexRange
): void => {
  validateIndexRange(
    indexRange,
    editedWords.length,
    originalWordTimings.length
  );

  const {editedStart, editedEnd, originalStart, originalEnd} = indexRange;
  const editedLength = editedEnd - editedStart;

  if (editedLength === 0) return;

  const originalStartTime = originalWordTimings[originalStart].start;
  const originalEndTime = originalWordTimings[originalEnd - 1].end;
  const totalDuration = originalEndTime - originalStartTime;

  const durationPerWord = totalDuration / editedLength;

  for (let i = 0; i < editedLength; i += 1) {
    const editedIndex = editedStart + i;
    editedWords[editedIndex].start = originalStartTime + i * durationPerWord;
    editedWords[editedIndex].end =
      originalStartTime + (i + 1) * durationPerWord;
  }
};

export const getUniformlyTimedWords = (
  caption: Caption,
  startIndex = 0,
  endIndex: number | null = null
): Word[] => {
  if (!caption?.text?.trim()) return [];
  if (caption.wordTimings) return caption.wordTimings;

  const wordsText = tokenizeWords(caption.text);

  const actualEndIndex = endIndex === null ? wordsText.length : endIndex;
  const wordsToTime = wordsText.slice(startIndex, actualEndIndex);

  if (wordsToTime.length === 0) return [];

  const totalDuration = caption.end - caption.start;
  const durationPerWord = totalDuration / wordsToTime.length;

  return wordsToTime.map((word, index) => ({
    text: word,
    start: caption.start + index * durationPerWord,
    end: caption.start + (index + 1) * durationPerWord,
  }));
};

export const getCurrentWord = (
  words: Word[],
  currentTime: number
): Word | undefined =>
  words.find((word) => currentTime >= word.start && currentTime < word.end);
